<script setup lang="ts">
/** Section « Contexte » (capture DT, section 3). */
import { BuildingOfficeIcon } from '@heroicons/vue/24/outline'
import type { AccompContext } from '@/v2/accompagnement/api/accompagnementTypes'
import {
  ACCOMP_GROWTH_OPTIONS,
  ACCOMP_REMOTE_WORK_OPTIONS,
} from '@/v2/accompagnement/constants/accompagnementConstants'

const context = defineModel<AccompContext>('context', { required: true })

function onNumberInput(key: 'sites' | 'collaborators', e: Event) {
  const raw = (e.target as HTMLInputElement).value
  context.value = { ...context.value, [key]: raw === '' ? null : Math.max(0, Number(raw)) }
}
</script>

<template>
  <section class="card border border-base-300 bg-base-100 shadow-sm" aria-labelledby="accomp-context-title">
    <div class="card-body gap-4 p-4 md:p-6">
      <h2 id="accomp-context-title" class="card-title flex items-center gap-2 text-lg font-semibold">
        <BuildingOfficeIcon class="h-5 w-5 text-primary" aria-hidden="true" />
        Contexte
      </h2>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="md:col-span-2">
          <label for="ctx-activity" class="mb-1 block text-sm font-medium text-base-content">
            Activité du client
          </label>
          <input
            id="ctx-activity"
            v-model="context.activity"
            type="text"
            class="input input-bordered w-full"
            placeholder="Ex. Cabinet médical, garage, transport…"
          />
        </div>

        <div>
          <label for="ctx-sites" class="mb-1 block text-sm font-medium text-base-content">Nombre de sites</label>
          <input
            id="ctx-sites"
            type="number"
            min="0"
            class="input input-bordered w-full"
            :value="context.sites ?? ''"
            @input="onNumberInput('sites', $event)"
          />
        </div>

        <div>
          <label for="ctx-collab" class="mb-1 block text-sm font-medium text-base-content">
            Nombre de collaborateurs
          </label>
          <input
            id="ctx-collab"
            type="number"
            min="0"
            class="input input-bordered w-full"
            :value="context.collaborators ?? ''"
            @input="onNumberInput('collaborators', $event)"
          />
        </div>

        <div>
          <label for="ctx-remote" class="mb-1 block text-sm font-medium text-base-content">Télétravail</label>
          <select id="ctx-remote" v-model="context.remoteWork" class="select select-bordered w-full">
            <option value="">—</option>
            <option v-for="opt in ACCOMP_REMOTE_WORK_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <div>
          <label for="ctx-growth" class="mb-1 block text-sm font-medium text-base-content">Croissance prévue</label>
          <select id="ctx-growth" v-model="context.growth" class="select select-bordered w-full">
            <option value="">—</option>
            <option v-for="opt in ACCOMP_GROWTH_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <div class="md:col-span-2">
          <label for="ctx-constraints" class="mb-1 block text-sm font-medium text-base-content">
            Contraintes particulières
          </label>
          <textarea
            id="ctx-constraints"
            v-model="context.constraints"
            rows="2"
            class="textarea textarea-bordered w-full text-base"
            placeholder="Horaires d'intervention, accès site, environnement spécifique…"
          ></textarea>
        </div>
      </div>
    </div>
  </section>
</template>
